import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { CurriculumCourseManagerGetter } from 'layers/content/v3/Hocs/CurriculumCourseManagerGetter'
import { CurriculumRouteContentGetter } from 'layers/content/v3/Hocs/CurriculumRouteContentGetter'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { resolveRouteServiceApp } from 'utils/productHelpers'

import LessonsComponent from './component'
import {
  courseSelectHandlerFactory,
  classSelectHandlerFactory,
  gotoDashboardHandlerFactory,
} from './handlers'
import { useLessonsActions, useLessonsPageData, useLessonsState } from './hooks'
import { DIGITAL_WELL_BEING_PRODUCT_NAME } from './constants'

export const LessonsContainer = props => {
  const {
    match,
    location,
    navigationEvent: navigationEventProp,
    courseManager,
    currentSite,
    currentSiteV4: currentSiteV4Prop,
    instructorData: instructorDataProp,
    productTabTitle: productTabTitleProp,
    userContext: userContextProp,
    // CreateCourse props
    lms: lmsProp,
    pathName: pathNameProp,
    userAccess: userAccessProp,
    userProfile: userProfileProp,
    createCourseInstanceV2ProgramFlex: createCourseInstanceV2ProgramFlexProp,
    createCourseInstanceByCourse: createCourseInstanceByCourseProp,
    deletingCourseCreatedStatus: deletingCourseCreatedStatusProp,
    deleteCourseInstance: deleteCourseInstanceProp,
    fetchInstructorData: fetchInstructorDataProp,
    updateCourseInstance: updateCourseInstanceProp,
  } = props || {}

  const activeSiteId = currentSite?.siteId
  const stateProps = useLessonsState(activeSiteId)
  const actions = useLessonsActions()

  const instructorData = instructorDataProp ?? stateProps.instructorData
  const userContext = userContextProp ?? stateProps.userContext
  const productTabTitle = productTabTitleProp ?? stateProps.productTabTitle
  const currentSiteV4 = currentSiteV4Prop ?? stateProps.currentSiteV4
  const lms = lmsProp ?? stateProps.lms
  const pathName = pathNameProp ?? stateProps.pathName
  const userAccess = userAccessProp ?? stateProps.userAccess
  const userProfile = userProfileProp ?? stateProps.userProfile
  const handleNavigationEvent = navigationEventProp ?? actions.navigationEvent
  const handleCreateCourseInstanceV2ProgramFlex =
    createCourseInstanceV2ProgramFlexProp ??
    actions.createCourseInstanceV2ProgramFlex
  const handleCreateCourseInstanceByCourse =
    createCourseInstanceByCourseProp ?? actions.createCourseInstanceByCourse
  const handleDeletingCourseCreatedStatus =
    deletingCourseCreatedStatusProp ?? actions.deletingCourseCreatedStatus
  const handleDeleteCourseInstance =
    deleteCourseInstanceProp ?? actions.deleteCourseInstance
  const handleFetchInstructorData =
    fetchInstructorDataProp ?? actions.fetchInstructorData
  const handleUpdateCourseInstance =
    updateCourseInstanceProp ?? actions.updateCourseInstance

  // TODO: FIX ONCE WE DO DYNAMIC COURSE FETCHING ON PAGE DATA FROM CONTENT SERVICE This whole containers methods of getting and selecting course info is needed until we make page data fetching for course structures dynamic. Also until we remove all the v2 to v1 lesson shaping we are doing

  // TODO LEARN-23339 Update this to be in the secondstep-learn/src/experienceComponents/CreateCourse/flag.on/container.js once we remove siteSelection flags. Need to update in dashboard as well.
  const {
    classes,
    courseLinks,
    courseManager: resolvedCourseManager,
    courseTreesWithDisplayTitles,
    lessonCompletionGoalValue,
    programKey,
  } = useLessonsPageData({
    courseManager,
    currentSite,
    instructorData,
    match,
    userContext,
  })

  const goToDashboard = useMemo(
    () => gotoDashboardHandlerFactory(handleNavigationEvent),
    [handleNavigationEvent],
  )
  const onClassClick = useMemo(
    () =>
      classSelectHandlerFactory(
        programKey,
        handleNavigationEvent,
        location?.search,
      ),
    [handleNavigationEvent, location?.search, programKey],
  )
  const onCourseClick = useMemo(
    () => courseSelectHandlerFactory(programKey, handleNavigationEvent),
    [handleNavigationEvent, programKey],
  )
  const showSmallGroupCreation = programKey !== DIGITAL_WELL_BEING_PRODUCT_NAME

  return (
    <LessonsComponent
      classes={classes}
      contentfulCourses={courseTreesWithDisplayTitles}
      courseLinks={courseLinks}
      courseManager={resolvedCourseManager}
      createCourseInstanceByCourse={handleCreateCourseInstanceByCourse}
      createCourseInstanceV2ProgramFlex={
        handleCreateCourseInstanceV2ProgramFlex
      }
      currentSiteV4={currentSiteV4}
      deleteCourseInstance={handleDeleteCourseInstance}
      deletingCourseCreatedStatus={handleDeletingCourseCreatedStatus}
      fetchInstructorData={handleFetchInstructorData}
      goToDashboard={goToDashboard}
      lessonCompletionGoalValue={lessonCompletionGoalValue}
      lms={lms}
      match={match}
      onClassClick={onClassClick}
      onComponentClick={handleNavigationEvent}
      onCourseClick={onCourseClick}
      pathName={pathName}
      productName={programKey}
      productTabTitle={productTabTitle}
      showSmallGroupCreation={showSmallGroupCreation}
      updateCourseInstance={handleUpdateCourseInstance}
      userAccess={userAccess}
      userContext={userContext}
      userProfile={userProfile}
    />
  )
}

export default withSiteContext(
  CurriculumCourseManagerGetter(
    CurriculumRouteContentGetter(LessonsContainer, {
      app: ({ route }) => resolveRouteServiceApp(route),
    }),
    {
      programResolver: () => 'none',
    },
  ),
)

LessonsContainer.propTypes = {
  courseManager: PropTypes.object,
  createCourseInstanceByCourse: PropTypes.func,
  createCourseInstanceV2ProgramFlex: PropTypes.func,
  currentSite: PropTypes.object,
  currentSiteV4: PropTypes.object,
  deleteCourseInstance: PropTypes.func,
  deletingCourseCreatedStatus: PropTypes.func,
  fetchInstructorData: PropTypes.func,
  instructorData: PropTypes.object,
  lms: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      productName: PropTypes.string,
      verb: PropTypes.string,
    }),
  }),
  navigationEvent: PropTypes.func,
  pathName: PropTypes.string,
  productTabTitle: PropTypes.string,
  updateCourseInstance: PropTypes.func,
  userAccess: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}
